import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Select, Button, Modal, Image,
  Tag, message, Progress, Space, DatePicker, Typography,
} from 'antd';
import {
  ReloadOutlined,
  RobotOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import api from '../config/api';

const { Title } = Typography;

const STATUS_CONFIG = {
  pending: { color: 'gold', icon: <ClockCircleOutlined />, text: 'Pendiente' },
  processing: { color: 'blue', icon: <SyncOutlined spin />, text: 'Procesando' },
  processed: { color: 'green', icon: <CheckCircleOutlined />, text: 'Procesado' },
  error: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Error' },
};

const DEPARTMENTS = [
  { code: '01', name: 'Hogar' }, { code: '02', name: 'Mercería' },
  { code: '03', name: 'Fiestas' }, { code: '04', name: 'Papelería' },
  { code: '05', name: 'Juguetes' }, { code: '06', name: 'Manualidades' },
  { code: '07', name: 'Ropa' }, { code: '08', name: 'Belleza' },
  { code: '09', name: 'Electrónica' }, { code: '10', name: 'Mascotas' },
  { code: '11', name: 'Temporada' }, { code: '12', name: 'No sé' },
];

const ProductDashboard = () => {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [photoModal, setPhotoModal] = useState(null);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/products/stats');
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchProducts = useCallback(async (p = page, f = filters) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20, ...f };
      const { data } = await api.get('/products', { params });
      setProducts(data.products);
      setTotal(data.total);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchStats(); fetchProducts(1); }, []);

  const handleFilter = (key, value) => {
    const newFilters = { ...filters };
    if (value) newFilters[key] = value;
    else delete newFilters[key];
    setFilters(newFilters);
    setPage(1);
    fetchProducts(1, newFilters);
  };

  const handlePageChange = (p) => {
    setPage(p);
    fetchProducts(p);
  };

  const handleViewPhotos = async (id) => {
    try {
      const { data } = await api.get(`/products/${id}`);
      setPhotoModal(data.product);
    } catch (err) {
      message.error('Error cargando fotos');
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Eliminar producto',
      content: 'Esta acción no se puede deshacer',
      okText: 'Eliminar',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/products/${id}`);
          message.success('Producto eliminado');
          fetchProducts();
          fetchStats();
        } catch (err) {
          message.error(err.response?.data?.message || 'Error al eliminar');
        }
      },
    });
  };

  const handleProcessOne = async (id) => {
    try {
      await api.post(`/products/${id}/process`);
      message.success('Producto procesado');
      fetchProducts();
      fetchStats();
    } catch (err) {
      message.error(err.response?.data?.message || 'Error al procesar');
    }
  };

  const handleBatchProcess = async () => {
    setBatchProcessing(true);
    setBatchProgress({ processed: 0, errors: 0, total: stats?.byStatus?.pending || 0 });
    try {
      const { data } = await api.post('/products/batch-process');
      setBatchProgress(data);
      message.success(`Procesados: ${data.processed}, Errores: ${data.errors}`);
      fetchProducts();
      fetchStats();
    } catch (err) {
      message.error('Error en batch process');
    } finally {
      setBatchProcessing(false);
    }
  };

  const columns = [
    {
      title: 'Producto',
      key: 'name',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.cleanName || r.name}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{r.barcode || 'Sin código'}</div>
        </div>
      ),
    },
    {
      title: 'Depto',
      dataIndex: ['department', 'name'],
      width: 100,
    },
    {
      title: 'Empleado',
      dataIndex: ['capturedBy', 'displayName'],
      width: 120,
      render: (v) => v?.split(' ')[0],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s) => {
        const cfg = STATUS_CONFIG[s] || STATUS_CONFIG.pending;
        return <Tag icon={cfg.icon} color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: 'Acciones',
      width: 160,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewPhotos(r._id)} />
          {r.status === 'pending' && (
            <>
              <Button size="small" icon={<RobotOutlined />} onClick={() => handleProcessOne(r._id)} />
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)} />
            </>
          )}
          {r.status === 'error' && (
            <Button size="small" icon={<RobotOutlined />} onClick={() => handleProcessOne(r._id)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Dashboard de Productos</Title>
        <Button icon={<ReloadOutlined />} onClick={() => { fetchStats(); fetchProducts(); }}>
          Actualizar
        </Button>
      </div>

      {/* Stats cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Hoy" value={stats?.today || 0} valueStyle={{ color: '#E8192C' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Total" value={stats?.total || 0} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Pendientes" value={stats?.byStatus?.pending || 0} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Procesados" value={stats?.byStatus?.processed || 0} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      {/* By department */}
      {stats?.byDepartment?.length > 0 && (
        <Card size="small" title="Por departamento" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {stats.byDepartment.map(d => (
              <Tag key={d.code} style={{ fontSize: 13 }}>{d.name}: <strong>{d.count}</strong></Tag>
            ))}
          </div>
        </Card>
      )}

      {/* By employee */}
      {stats?.byEmployee?.length > 0 && (
        <Card size="small" title="Por empleado" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {stats.byEmployee.map(e => (
              <Tag key={e.email} color="blue" style={{ fontSize: 13 }}>
                {e.name?.split(' ')[0]}: <strong>{e.count}</strong>
              </Tag>
            ))}
          </div>
        </Card>
      )}

      {/* Batch process */}
      <Card size="small" style={{ marginBottom: 16, borderColor: '#E8192C' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span>
            <strong>{stats?.byStatus?.pending || 0}</strong> productos pendientes de procesar
          </span>
          <Button
            type="primary"
            danger
            icon={<RobotOutlined />}
            loading={batchProcessing}
            onClick={handleBatchProcess}
            disabled={!stats?.byStatus?.pending}
          >
            Procesar todos con IA
          </Button>
        </div>
        {batchProgress && (
          <Progress
            percent={batchProgress.total ? Math.round(((batchProgress.processed + batchProgress.errors) / batchProgress.total) * 100) : 0}
            status={batchProgress.errors > 0 ? 'exception' : 'active'}
            style={{ marginTop: 8 }}
          />
        )}
      </Card>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <Select
          allowClear
          placeholder="Departamento"
          style={{ width: 150 }}
          onChange={v => handleFilter('department', v)}
          options={DEPARTMENTS.map(d => ({ value: d.code, label: d.name }))}
        />
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 130 }}
          onChange={v => handleFilter('status', v)}
          options={[
            { value: 'pending', label: 'Pendiente' },
            { value: 'processing', label: 'Procesando' },
            { value: 'processed', label: 'Procesado' },
            { value: 'error', label: 'Error' },
          ]}
        />
        {stats?.byEmployee?.length > 0 && (
          <Select
            allowClear
            placeholder="Empleado"
            style={{ width: 150 }}
            onChange={v => handleFilter('employee', v)}
            options={stats.byEmployee.map(e => ({ value: e.email, label: e.name?.split(' ')[0] }))}
          />
        )}
      </div>

      {/* Table */}
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={products}
        loading={loading}
        size="small"
        scroll={{ x: 600 }}
        pagination={{
          current: page,
          total,
          pageSize: 20,
          onChange: handlePageChange,
          showSizeChanger: false,
          showTotal: (t) => `${t} productos`,
        }}
      />

      {/* Photo modal */}
      <Modal
        open={!!photoModal}
        onCancel={() => setPhotoModal(null)}
        footer={null}
        title={photoModal?.cleanName || photoModal?.name}
        width={600}
      >
        {photoModal && (
          <div>
            <Row gutter={[8, 8]}>
              <Col span={8}>
                <div style={{ textAlign: 'center', marginBottom: 4, fontWeight: 500 }}>Frente</div>
                <Image src={photoModal.photoFront} style={{ width: '100%' }} />
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center', marginBottom: 4, fontWeight: 500 }}>Barcode</div>
                <Image src={photoModal.photoBarcode} style={{ width: '100%' }} />
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center', marginBottom: 4, fontWeight: 500 }}>Atrás</div>
                {photoModal.photoBack ? (
                  <Image src={photoModal.photoBack} style={{ width: '100%' }} />
                ) : (
                  <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>Sin foto</div>
                )}
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <p><strong>Barcode:</strong> {photoModal.barcode || 'N/A'}</p>
              <p><strong>Departamento:</strong> {photoModal.department?.name}</p>
              <p><strong>Capturado por:</strong> {photoModal.capturedBy?.displayName}</p>
              <p><strong>Status:</strong> {photoModal.status}</p>
              {photoModal.cleanName && <p><strong>Nombre limpio:</strong> {photoModal.cleanName}</p>}
              {photoModal.descriptionSale && <p><strong>Descripción:</strong> {photoModal.descriptionSale}</p>}
              {photoModal.odooId && <p><strong>Odoo ID:</strong> {photoModal.odooId}</p>}
              {photoModal.errorMessage && <p style={{ color: 'red' }}><strong>Error:</strong> {photoModal.errorMessage}</p>}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductDashboard;
